/**
 * @deprecated This file is deprecated and in migration to the new analytics architecture.
 * @see {@link ../analytics/README.md} for the new analytics system.
 * @todo Migrate to the new analytics system in src/core/analytics/
 */
import { useRecoilValue, useSetRecoilState } from "recoil";

import { findUser, getMetaFbc, getMetaFbp } from "@core/data/analytics-repository";
import { trackingParamsState } from "@core/state/analytics-state";
import { PartialWindow } from "@func-core/core/types";
import { getAnonymousId } from "@func-core/core/utils/browser-features";
import { getCookieValue, getCurrentURLSearchParams, getWindow } from "@utils/browser-features";
import { handleFindUserErrors } from "@utils/error-utils/catch-error-handlers";

type Analytics = {
  reset: () => void;
  load: (writeKey: string) => void;
  identify: (
    userId: string,
    user: Record<string, unknown>,
    options: { anonymousId: string }
  ) => void;
  setAnonymousId?: (id: string) => void;
};

export const useSetupAnalytics = async (): Promise<void> => {
  const w: Window | null = getWindow();
  const writeKey = process.env.GATSBY_SEGMENT_WRITE_KEY;
  const currentURLSearchParams = getCurrentURLSearchParams();

  if (!w || !writeKey) {
    return;
  }

  const analytics = w.analytics as Analytics;
  const ajsUserId = getCookieValue("ajs_user_id");
  const petParentKbKey = currentURLSearchParams?.get("pet_parent_kb_key");
  const userId = ajsUserId ? ajsUserId : petParentKbKey;
  const anonymousId = getAnonymousId(w as PartialWindow);

  if (anonymousId) analytics.setAnonymousId?.(anonymousId);

  if (!userId) {
    analytics.load(writeKey);

    return;
  }

  try {
    const user = await findUser(userId);

    analytics.identify(userId, user, { anonymousId });
    analytics.load(writeKey);
  } catch (error) {
    handleFindUserErrors(error as Error, "setupAnalytics", userId);

    analytics.load(writeKey);
  }
};

export const useGetTrackingParams = (): (() => void) => {
  const trackingParamsSetter = useSetRecoilState(trackingParamsState);
  const metaFbp = getMetaFbp();
  const metaFbc = getMetaFbc();

  return (): void => {
    trackingParamsSetter({ metaFbp, metaFbc });
  };
};

export const useTrackingParams = (): Record<string, unknown> | null => {
  return useRecoilValue(trackingParamsState);
};
